import React, { useContext, createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Shell from '../design/Shell';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Create Notifications Context (preserved from old Layout)
export const NotificationsContext = createContext();

// Header context — lets pages configure breadcrumb/actions in the topbar
export const PageHeaderContext = createContext({ setHeader: () => {} });

export function usePageHeader(breadcrumb, actions, deps = []) {
  const { setHeader } = useContext(PageHeaderContext);
  useEffect(() => {
    setHeader({ breadcrumb, actions });
    return () => setHeader({ breadcrumb: [], actions: null });
    // eslint-disable-next-line
  }, deps);
}

export default function Layout({ children }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [header, setHeaderState] = useState({ breadcrumb: [], actions: null });

  const setHeader = useCallback((h) => setHeaderState(h || { breadcrumb: [], actions: null }), []);

  useEffect(() => {
    fetchNotifications();

    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    const handleSWMessage = (event) => {
      if (event.data && event.data.type === 'NAVIGATE') {
        const taskId = event.data.taskId;
        const url = event.data.url;
        if (taskId) {
          navigate(`/tasks?openTask=${taskId}`);
        } else if (url) {
          navigate(url);
        }
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
    }

    return () => {
      clearInterval(interval);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
    };
    // eslint-disable-next-line
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      const unread = notifications.filter(n => !n.read);
      setNotifications([]);
      await Promise.all(unread.map(n => axios.patch(`${API}/notifications/${n.id}/read`)));
      toast.success('Tüm bildirimler temizlendi');
    } catch (error) {
      console.error(error);
      toast.error('Bildirimler temizlenemedi');
      fetchNotifications();
    }
  };

  const handleNotificationClick = async (notif) => {
    await markAsRead(notif.id);
    setShowNotifications(false);
    if (notif.taskId) {
      navigate(`/tasks?openTask=${notif.taskId}`);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const notificationsValue = {
    notifications,
    showNotifications,
    setShowNotifications,
    unreadCount,
    handleNotificationClick,
    clearAllNotifications,
  };

  return (
    <NotificationsContext.Provider value={notificationsValue}>
      <PageHeaderContext.Provider value={{ setHeader }}>
        <Shell breadcrumb={header.breadcrumb} actions={header.actions}>
          {children}
        </Shell>
      </PageHeaderContext.Provider>
    </NotificationsContext.Provider>
  );
}
